<!--
  ChatIntegration.vue
  -------------------
  Admin info page that explains how to embed the Tibtix Chat into a tenant's
  own website. Three integration modes (floating / hidden / inline) are
  documented; a small configurator generates a copy-pasteable <script> tag
  that already contains the tenant's correct TENANT_ID.

  This page does NOT yet load the actual widget.js — that part follows in a
  later step. The embed snippets here are the contract the future widget.js
  needs to fulfill.
-->
<template>
  <div class="integration-overlay" @click.self="$emit('close')">
    <div class="integration-card">

      <header class="integration-header">
        <h2>{{ t('chatIntegration') }}</h2>
        <button class="close-btn" @click="$emit('close')" aria-label="Close">&#10005;</button>
      </header>

      <p class="intro">
        {{ locale === 'en'
            ? 'Use the snippets below to embed the Tibtix Chat into your own website. Pick one of the three integration modes.'
            : 'Mit den folgenden Code-Schnipseln können Sie den Tibtix Chat in Ihre eigene Website einbauen. Wählen Sie eine der drei Integrationsvarianten.' }}
      </p>

      <!-- ===== Configurator ===== -->
      <section class="config-section">
        <h3>{{ locale === 'en' ? 'Configuration' : 'Konfiguration' }}</h3>

        <div class="config-grid">
          <label class="field">
            <span>{{ locale === 'en' ? 'Account' : 'Account' }}</span>
            <input type="text" v-model="config.account" />
          </label>

          <label class="field">
            <span>{{ locale === 'en' ? 'Mode' : 'Modus' }}</span>
            <select v-model="config.mode">
              <option value="floating">floating</option>
              <option value="hidden">hidden</option>
              <option value="inline">inline</option>
            </select>
          </label>

          <label class="field" v-if="config.mode === 'floating'">
            <span>{{ locale === 'en' ? 'Position' : 'Position' }}</span>
            <select v-model="config.position">
              <option value="br">br — {{ locale === 'en' ? 'bottom right' : 'unten rechts' }}</option>
              <option value="bl">bl — {{ locale === 'en' ? 'bottom left' : 'unten links' }}</option>
              <option value="tr">tr — {{ locale === 'en' ? 'top right' : 'oben rechts' }}</option>
              <option value="tl">tl — {{ locale === 'en' ? 'top left' : 'oben links' }}</option>
            </select>
          </label>

          <label class="field" v-if="config.mode === 'floating'">
            <span>{{ locale === 'en' ? 'Color' : 'Farbe' }}</span>
            <input type="color" v-model="config.color" />
          </label>

          <label class="field">
            <span>{{ locale === 'en' ? 'Language' : 'Sprache' }}</span>
            <select v-model="config.language">
              <option value="auto">{{ locale === 'en' ? 'Auto (browser)' : 'Automatisch (Browser)' }}</option>
              <option value="de">de — Deutsch</option>
              <option value="en">en — English</option>
              <option value="fr">fr — Français</option>
              <option value="it">it — Italiano</option>
            </select>
          </label>

          <label class="field" v-if="config.mode === 'floating'">
            <span>{{ locale === 'en' ? 'Title' : 'Titel' }}</span>
            <input type="text" v-model="config.title" />
          </label>

          <label class="field" v-if="config.mode === 'floating'">
            <span>{{ locale === 'en' ? 'Button text' : 'Button-Text' }}</span>
            <input type="text" v-model="config.buttonText" />
          </label>

          <label class="field checkbox" v-if="config.mode === 'floating'">
            <input type="checkbox" v-model="config.showBadge" />
            <span>{{ locale === 'en' ? 'Show unread badge' : 'Badge für ungelesene Nachrichten' }}</span>
          </label>

          <label class="field" v-if="config.mode === 'inline'">
            <span>{{ locale === 'en' ? 'Container ID' : 'Container-ID' }}</span>
            <input type="text" v-model="config.target" />
          </label>

          <label class="field">
            <span>z-Index</span>
            <input type="number" v-model.number="config.zIndex" min="0" />
          </label>
        </div>

        <h3>{{ locale === 'en' ? 'Generated embed code' : 'Generierter Einbettungs-Code' }}</h3>
        <pre class="code-block"><code>{{ generatedCode }}</code></pre>
        <button class="copy-btn" @click="copyCode">
          {{ copied
              ? (locale === 'en' ? 'Copied!' : 'Kopiert!')
              : (locale === 'en' ? 'Copy code' : 'Code kopieren') }}
        </button>
        <div class="tenant-hint">
          {{ locale === 'en' ? 'Tenant:' : 'Mandant:' }} <code>{{ tenantValue }}</code>
        </div>
      </section>

      <!-- ===== Mode 1: Floating ===== -->
      <section class="mode-section">
        <h3>1. {{ locale === 'en' ? 'Floating chat button' : 'Schwebender Chat-Button' }}</h3>
        <p>
          {{ locale === 'en'
              ? 'A floating chat icon appears in the corner of every page. Clicking it opens the chat as an overlay. Standard case, simplest integration.'
              : 'Ein schwebendes Chat-Icon erscheint in einer Ecke jeder Seite. Beim Klick öffnet sich der Chat als Overlay. Standardfall, einfachste Integration.' }}
        </p>
        <pre class="code-block"><code>{{ floatingExample }}</code></pre>
        <p class="hint">
          <strong>{{ locale === 'en' ? 'Positions:' : 'Positionen:' }}</strong>
          <code>br</code> = {{ locale === 'en' ? 'bottom right' : 'unten rechts' }},
          <code>bl</code> = {{ locale === 'en' ? 'bottom left' : 'unten links' }},
          <code>tr</code> = {{ locale === 'en' ? 'top right' : 'oben rechts' }},
          <code>tl</code> = {{ locale === 'en' ? 'top left' : 'oben links' }}.
        </p>
      </section>

      <!-- ===== Mode 2: Hidden / own button ===== -->
      <section class="mode-section">
        <h3>2. {{ locale === 'en' ? 'Menu link or your own button' : 'Menü-Link oder eigener Button' }}</h3>
        <p>
          {{ locale === 'en'
              ? 'No floating button is shown. Instead, your own menu link or button opens the chat via the JavaScript API.'
              : 'Es wird kein Floating-Button angezeigt. Stattdessen öffnet ein eigener Menü-Link oder Button den Chat über die JavaScript-API.' }}
        </p>
        <pre class="code-block"><code>{{ hiddenExample }}</code></pre>
        <p class="hint">
          {{ locale === 'en' ? 'Then trigger from your own UI:' : 'Anschließend aus eigenem UI auslösen:' }}
        </p>
        <pre class="code-block"><code>{{ hiddenLinkExample }}</code></pre>
        <pre class="code-block"><code>{{ hiddenButtonExample }}</code></pre>
        <p class="hint">
          {{ locale === 'en' ? 'Available globally:' : 'Global verfügbar:' }}
          <code>window.TibtixChat.open()</code>,
          <code>window.TibtixChat.close()</code>,
          <code>window.TibtixChat.toggle()</code>.
        </p>
      </section>

      <!-- ===== Mode 3: Inline ===== -->
      <section class="mode-section">
        <h3>3. {{ locale === 'en' ? 'Inline chat' : 'Inline-Chat' }}</h3>
        <p>
          {{ locale === 'en'
              ? 'The chat is rendered directly inside a container element on your page. No floating button is shown.'
              : 'Der Chat wird direkt in einen Container auf der Seite eingebettet. Es erscheint kein Floating-Button.' }}
        </p>
        <pre class="code-block"><code>{{ inlineExample }}</code></pre>
      </section>

    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useI18n } from '../i18n.js'

const props = defineProps({
  tenantId: { type: String, required: true }
})

defineEmits(['close'])

const { t, locale } = useI18n()

/* widget.js will be served from the same origin/path as this UI; the snippet
   the admin copies points exactly there. */
const widgetUrl = `${window.location.origin}/tt/chat-ui/widget.js`

const config = reactive({
  mode: 'floating',
  // Default account used to build the "account##domain" tenant value the widget passes
  // to the chat (required for the anonymous /temp/start flow).
  account: 'info',
  position: 'br',
  color: '#1f7ae0',
  title: 'Chat mit uns',
  buttonText: 'Chat',
  showBadge: true,
  target: 'tibtix-chat',
  language: 'auto',
  zIndex: 9999
})

/* The widget's data-tenant must be a full "account##domain". tenantId is the bare domain. */
const tenantValue = computed(() => `${config.account}##${props.tenantId}`)

function escapeAttr(value) {
  return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

// Builds the embed snippet (a script tag with one attribute per line). The
// closing tag is assembled via string concatenation so the literal sequence
// for ending a script element never appears in this Vue SFC source — the
// SFC parser would otherwise treat it as the end of this setup block.
function buildScript(attrs) {
  const lines = Object.entries(attrs)
      .filter(([, v]) => v !== '' && v != null)
      .map(([k, v]) => `  ${k}="${escapeAttr(v)}"`)
  return `<script\n${lines.join('\n')}>\n<` + `/script>`
}

const generatedCode = computed(() => {
  const attrs = {
    src: widgetUrl,
    'data-tenant': tenantValue.value,
    'data-mode': config.mode
  }
  if (config.mode === 'floating') {
    attrs['data-position'] = config.position
    attrs['data-color'] = config.color
    attrs['data-title'] = config.title
    attrs['data-button-text'] = config.buttonText
    attrs['data-show-badge'] = String(config.showBadge)
  }
  if (config.mode === 'inline') {
    attrs['data-target'] = config.target
  }
  if (config.language && config.language !== 'auto') {
    attrs['data-language'] = config.language
  }
  if (config.zIndex && config.zIndex !== 9999) {
    attrs['data-z-index'] = String(config.zIndex)
  }

  if (config.mode === 'inline') {
    return `<div id="${escapeAttr(config.target)}"></div>\n\n${buildScript(attrs)}`
  }
  return buildScript(attrs)
})

/* Static example snippets (independent of the configurator state) so each
   section shows the canonical form regardless of what the admin currently
   selected above. */
const floatingExample = computed(() => buildScript({
  src: widgetUrl,
  'data-tenant': tenantValue.value,
  'data-mode': 'floating',
  'data-position': 'br',
  'data-color': '#1f7ae0',
  'data-title': 'Chat mit uns',
  'data-button-text': 'Chat',
  'data-show-badge': 'true'
}))

const hiddenExample = computed(() => buildScript({
  src: widgetUrl,
  'data-tenant': tenantValue.value,
  'data-mode': 'hidden'
}))

const hiddenLinkExample =
    `<a href="#" onclick="TibtixChat.open(); return false;">Kundenchat</a>`
const hiddenButtonExample =
    `<button onclick="TibtixChat.open()">Chat öffnen</button>`

const inlineExample = computed(() => {
  const script = buildScript({
    src: widgetUrl,
    'data-tenant': tenantValue.value,
    'data-mode': 'inline',
    'data-target': 'tibtix-chat'
  })
  return `<div id="tibtix-chat"></div>\n\n${script}`
})

const copied = ref(false)
let copyTimeout = null

async function copyCode() {
  const text = generatedCode.value
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
    } else {
      // Fallback for non-secure contexts (e.g. http during dev)
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    copied.value = true
    if (copyTimeout) clearTimeout(copyTimeout)
    copyTimeout = setTimeout(() => { copied.value = false }, 1800)
  } catch (e) {
    /* ignore — user can still copy manually */
  }
}
</script>

<style scoped>
.integration-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 110;
  overflow-y: auto;
  padding: 24px 16px;
  -webkit-overflow-scrolling: touch;
}

.integration-card {
  width: min(820px, 100%);
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #1a1a2e;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.integration-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.integration-header h2 {
  margin: 0;
  font-size: 20px;
}

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px 8px;
  border-radius: 6px;
}
.close-btn:hover { background: #f0f2f5; color: #000; }

.intro {
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
}

.config-section,
.mode-section {
  border: 1px solid #e4e8ee;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fafbfd;
}

.config-section h3,
.mode-section h3 {
  margin: 0 0 4px;
  font-size: 15px;
  color: #1a1a2e;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #374151;
}
.field > span { font-weight: 500; }
.field.checkbox {
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: end;
}
.field.checkbox > span { font-weight: 400; }

.field input[type="text"],
.field input[type="number"],
.field select {
  font: inherit;
  padding: 7px 9px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}
.field input[type="color"] {
  width: 100%;
  height: 34px;
  padding: 2px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}
.field input:focus,
.field select:focus {
  outline: 2px solid #4f46e5;
  border-color: #4f46e5;
}

.code-block {
  margin: 0;
  padding: 12px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre;
}
.code-block code { background: none; padding: 0; }

.copy-btn {
  align-self: flex-start;
  border: none;
  background: #4f46e5;
  color: white;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}
.copy-btn:hover { background: #4338ca; }

.tenant-hint {
  font-size: 12px;
  color: #6b7280;
}
.tenant-hint code,
.hint code {
  background: #eef2ff;
  color: #4338ca;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 12px;
}

.hint {
  margin: 0;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
}

p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
}

/* ===== Mobile ===== */
@media (max-width: 640px) {
  .integration-overlay {
    padding: 0;
    align-items: stretch;
  }
  .integration-card {
    width: 100%;
    border-radius: 0;
    min-height: 100dvh;
    padding: 16px;
    padding-top: max(16px, env(safe-area-inset-top));
    padding-bottom: max(16px, env(safe-area-inset-bottom));
    gap: 14px;
  }
  .integration-header h2 { font-size: 17px; }
  .config-grid { grid-template-columns: 1fr; }
  .code-block { font-size: 11px; padding: 10px; }
  .config-section,
  .mode-section { padding: 12px; }
}
</style>
